/**
 * `insert_image` et `edit_image` — poser une image dans le corps, et retirer,
 * redimensionner ou remplacer UNE image du corps (#2141).
 *
 * Une image visible ne disparaît jamais par effet de bord d'un autre verbe :
 * c'est ici, et seulement ici, qu'un agent la retire, sur un identifiant qu'il
 * a lu (`canvas_inspect`, `imagesDuCorps.ts`). D'où viennent les octets d'une
 * image posée ou substituée : `sourceDImage.ts`.
 *
 * Déplacer une image n'est pas un verbe : l'agent pose une copie
 * (`insert_image`, source `{imageId}`) puis retire l'originale. `images.move`
 * répond `success:true` sans rien déplacer et `blocks.move` refuse un
 * paragraphe qui porte une image (mesurés le 2026-10-12) ; deux gestes que
 * l'agent compose se vérifient un par un, un verbe unique pourrait s'arrêter
 * entre les deux.
 *
 * ## Mesuré sur superdoc@2.15.1 (2026-10-12, `mesure-images-du-corps.spec.ts`)
 *
 * - `images.delete({imageId}, {changeMode})` : en direct, une entrée
 *   d'historique, l'image quitte `images.list` et le fichier exporté ; en suivi,
 *   une révision `structural-image-deletion`, l'image quitte `images.list` et la
 *   projection `final` tout de suite, le fichier la garde sous `w:del`, et
 *   rejeter la révision la rend. `capabilities.get()` la déclare indisponible.
 * - `images.setSize({imageId, size:{width,height,unit:'px'}}, {changeMode})` :
 *   relecture conforme ; en suivi, une révision `structural-image-property-change`
 *   (le fichier porte l'ancien dessin sous `w:del` et le nouveau sous `w:ins`).
 *   Sur une taille déjà en place elle rend `success:true, changed:false` sans
 *   rien écrire : le verbe refuse ce cas avant d'appeler l'éditeur.
 * - L'identifiant d'une image ne change ni d'une liste à l'autre ni après un
 *   changement de taille.
 * - Après un retrait DIRECT, `blocks.delete` du paragraphe vidé passe. Après un
 *   retrait SUIVI, il lève `paragraph-tracked-wrapper-unsupported` tant que la
 *   révision n'est pas décidée : le reçu dit laquelle des deux suites vaut.
 *
 *
 * ## Poser et remplacer (même sonde, M6 à M9)
 *
 * - `create.image({src, alt, size:{width,height,unit:'px'}, at:{kind:'before'|'after',
 *   target:{kind:'block',nodeType,nodeId}}}, {changeMode})` écrit un paragraphe
 *   NEUF, à l'endroit demandé, qui porte l'image en ligne à la taille demandée, et
 *   un média neuf aux octets de la source. En direct : trois entrées d'historique,
 *   aucune révision. En suivi : une entrée, une révision `structural-image-insertion`.
 *   Annuler ces entrées retire l'image, son paragraphe et son média du fichier.
 * - ⚠️ **Sa réponse ne dit rien de fiable.** En suivi elle nomme une AUTRE image
 *   que celle qu'elle vient de poser (`image.nodeId` = le cachet déjà présent) ;
 *   sur un modèle réel de cabinet elle LÈVE après avoir écrit (`WorkerTransportError:
 *   UTF-8 offset must be an integer in […]`, relevé du 2026-10-12, non reproduit sur
 *   l'acte de banc, avec ou sans pied de page). Le succès se décide donc sur
 *   `images.list` relue : exactement une image neuve, dans un bloc neuf, à sa place.
 * - `images.replaceSource({imageId, src}, {changeMode})` garde l'identifiant, le
 *   placement et le cadre (largeur ET hauteur) : une image d'autres proportions y
 *   est étirée. Une entrée d'historique ; en suivi, une révision
 *   `structural-image-replacement`. La même image redonnée est réécrite sans refus.
 * - ⚠️ **L'ordre « taille, puis contenu » garde l'identifiant ; l'ordre inverse le
 *   change sous suivi.** Après un remplacement SUIVI, tout `images.setSize` suivi
 *   fait relister l'image sous un identifiant neuf (`img:301:…:rIdAncien` devient
 *   `img:301:…:rId9`). Le remplacement règle donc la hauteur du cadre AVANT de
 *   changer le contenu ; et un changement de taille demandé plus tard retrouve
 *   l'image sous son identifiant neuf (`imageRelue`). En direct l'identifiant ne
 *   change dans aucun ordre.
 * - ⚠️ **Un SECOND remplacement suivi de la même image change l'identifiant entre
 *   ses deux écritures** (`canvas-images.spec.ts`, « deux fois sous suivi ») : son
 *   `images.setSize` est un changement de taille après un remplacement suivi, donc
 *   l'image est relistée sous un identifiant neuf, et `images.replaceSource` sur
 *   l'identifiant demandé répond `TARGET_NOT_FOUND` (l'ancienne image reste, étirée
 *   dans le nouveau cadre). Le remplacement relit donc la liste après le cadre,
 *   retrouve l'image (`imageRelue`), écrit le contenu sous l'identifiant qui vaut
 *   alors, et le reçu porte cet identifiant.
 *
 * Non mesuré : une image dans une zone de texte, une image dont l'éditeur ne
 * publie pas la taille, une image posée avant ou après un tableau ou dans une
 * cellule, le jet de `create.image` sur l'acte de banc.
 */

import type { CanvasActionInput, CanvasActionName, CanvasImageReceipt, CanvasInspectImage } from '@hector/shared-types';
import { LARGEUR_UTILE_DU_CANVAS_MM } from '../../editor/canvasLetterhead';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
} from './actionReceipt';
import { isCanvasProjectionReadError, lireProjectionMarkdown, type CanvasMarkdownProjection } from './canvasProjection';
import { idsDeRevision, operationDuDocument, type CanvasSnapshot } from './canvasSnapshot';
import { estArretDeLot } from './executionAsynchrone';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { imagesDuCorps, listerLesImages, mediasDesImages, type ImageDuCorps, type ImageListee } from './imagesDuCorps';
import { resolveCanvasSelector } from './selectorResolver';
import { resoudreLaSourceDImage } from './sourceDImage';
import { modeDeRevision, snapshot, type CanvasActionContext } from './textActionExecutors';

type EditImageInput = Extract<CanvasActionInput, { action: 'edit_image' }>;
type InsertImageInput = Extract<CanvasActionInput, { action: 'insert_image' }>;
type Taille = { width: number; height: number };

/**
 * Largeur du texte sur la page du gabarit, en pixels à 96 par pouce : une image
 * posée sans largeur demandée ne la dépasse pas. La source est celle de
 * l'en-tête du cabinet (`canvasLetterhead.ts`).
 */
const LARGEUR_DU_TEXTE_PX = Math.round((LARGEUR_UTILE_DU_CANVAS_MM * 96) / 25.4);

/** Écart toléré entre la taille demandée et la taille relue, en pixels : l'éditeur convertit par des unités Word entières. */
const TOLERANCE_PX = 1;
/** Images nommées dans un refus : assez pour retrouver la bonne, sans recopier une planche entière. */
const IMAGES_NOMMEES_AU_PLUS = 10;

const tailleDe = (image: CanvasInspectImage): Taille => ({ width: image.width, height: image.height });
const enClair = (taille: Taille): string => `${taille.width}×${taille.height} px`;
const memeTaille = (a: Taille, b: Taille): boolean => Math.abs(a.width - b.width) <= TOLERANCE_PX && Math.abs(a.height - b.height) <= TOLERANCE_PX;

function nommerLesImages(images: readonly ImageDuCorps[]): string {
  if (images.length === 0) return 'The document body carries no image the editor lists.';
  const nommees = images.slice(0, IMAGES_NOMMEES_AU_PLUS).map((image) => `${image.imageId} (block ${image.nodeId}, ${enClair(image)}, ${image.placement})`);
  const reste = images.length - nommees.length;
  return `Body images: ${nommees.join('; ')}${reste > 0 ? `; and ${reste} more` : ''}.`;
}

/**
 * La taille à écrire : les côtés donnés, arrondis au pixel, et pour un côté
 * omis celui que donnent les proportions lues.
 *
 * @returns `undefined` quand un seul côté est donné et que la taille lue a un
 *   côté nul : il n'y a pas de proportion à garder.
 */
function tailleDemandee(demande: NonNullable<EditImageInput['size']>, lue: Taille): Taille | undefined {
  const width = demande.width === undefined ? undefined : Math.round(demande.width);
  const height = demande.height === undefined ? undefined : Math.round(demande.height);
  if (width !== undefined && height !== undefined) return { width, height };
  if (lue.width <= 0 || lue.height <= 0) return undefined;
  if (width !== undefined) return { width, height: Math.max(1, Math.round((width * lue.height) / lue.width)) };
  if (height !== undefined) return { width: Math.max(1, Math.round((height * lue.width) / lue.height)), height };
  return undefined;
}

/** Les images et la carte du corps, lues ensemble : ce que le verbe sait du document avant d'écrire. */
async function lireLesImagesEtLeCorps(ctx: CanvasActionContext, verbe: 'insert_image' | 'edit_image'): Promise<
  | { ok: true; toutes: ImageListee[]; duCorps: ImageDuCorps[]; projection: CanvasMarkdownProjection }
  | { ok: false; code: string; message: string }
> {
  const toutes = await listerLesImages(ctx.doc);
  if (toutes === undefined) {
    return { ok: false, code: 'CAPABILITY_UNAVAILABLE', message: `${verbe} requires editor.doc.images.list to identify the images of the document, and this document does not expose it. Nothing was written. Tell the lawyer images cannot be changed from here.` };
  }
  try {
    const projection = await lireProjectionMarkdown(ctx.doc, 'final');
    return { ok: true, toutes, duCorps: imagesDuCorps(toutes, projection.blocsDuCorps), projection };
  } catch (erreur) {
    if (!isCanvasProjectionReadError(erreur)) throw erreur;
    return { ok: false, code: erreur.code, message: `${verbe} could not read which blocks belong to the document body, so it cannot tell a body image from a header or footer image. Nothing was written. ${erreur.message} Run canvas_inspect again, then resend the action.` };
  }
}

function refus(
  input: { action: CanvasActionName },
  pre: CanvasSnapshot,
  code: string,
  message: string,
  recovery: NonNullable<CanvasActionReceipt['recovery']>,
): CanvasActionReceipt {
  return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code, message, recovery });
}

/** Ce que le paragraphe porteur est devenu après un retrait, dit avec la suite que le régime autorise. */
function suiteApresRetrait(nodeId: string, projection: CanvasMarkdownProjection | undefined, restantes: readonly ImageDuCorps[], suivi: boolean): string {
  const bloc = projection?.blocks.find((candidat) => candidat.identity === 'public' && candidat.nodeId === nodeId);
  if (bloc === undefined) return `Block ${nodeId} could not be read back: run canvas_inspect on it to see what it still carries.`;
  if (bloc.fullText.trim() !== '' || restantes.some((image) => image.nodeId === nodeId)) {
    return `Block ${nodeId} still carries other content and stays in place.`;
  }
  return suivi
    ? `Block ${nodeId} now reads empty, but the editor cannot delete it while this revision is pending: tell the lawyer the empty paragraph goes once they accept the revision, and do not call delete_block_range on it before.`
    : `Block ${nodeId} is now an empty paragraph: remove it with delete_block_range {from:{kind:"nodeId",nodeId:"${nodeId}"}} unless the lawyer wants the blank line kept.`;
}

/** Profondeur de la pile d'annulation, ou `undefined` quand l'éditeur ne la publie pas. */
async function profondeurDHistorique(ctx: CanvasActionContext): Promise<number | undefined> {
  const lire = operationDuDocument(ctx.doc, 'history', 'get');
  if (lire === undefined) return undefined;
  try {
    const etat: unknown = await lire();
    const profondeur = typeof etat === 'object' && etat !== null ? (etat as { undoDepth?: unknown }).undoDepth : undefined;
    return typeof profondeur === 'number' ? profondeur : undefined;
  } catch {
    return undefined;
  }
}

/**
 * La phrase d'annulation du reçu : le nombre d'entrées d'historique que l'appel
 * a réellement laissées, lu avant et après ; à défaut de lecture, le nombre
 * mesuré pour ce geste (en-tête du module).
 */
function annulation(avant: number | undefined, apres: number | undefined, mesure: number): string {
  const pas = avant !== undefined && apres !== undefined && apres > avant ? apres - avant : mesure;
  return `To undo this call: undo_changes steps:${pas}.`;
}

/**
 * Un appel d'écriture d'image, sous les gardes communes. Une exception de
 * l'éditeur est rendue comme un refus : il lève certains refus au lieu de les
 * rendre (`WorkerTransportError`), et `create.image` peut lever APRÈS avoir écrit.
 */
async function ecrire(ctx: CanvasActionContext, etape: string, attendue: CanvasSnapshot['revision'], appel: (options: Record<string, unknown>) => unknown): Promise<unknown> {
  const options = { changeMode: modeDeRevision(ctx), expectedRevision: attendue };
  try {
    return await muterSousFences(ctx, etape, options, () => appel(options));
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    return { success: false, failure: { code: 'PROVIDER_EXCEPTION', message: (erreur instanceof Error ? erreur.message : String(erreur)).slice(0, 200) } };
  }
}

const aRefuse = (reponse: unknown): boolean => typeof reponse === 'object' && reponse !== null && (reponse as { success?: unknown }).success === false;

/**
 * L'image après l'écriture : sous son identifiant, ou, quand l'éditeur l'a
 * relistée sous un identifiant neuf (changement de taille suivi d'une image au
 * contenu remplacé sous suivi, mesuré), la seule image neuve du même bloc.
 */
function imageRelue(imageId: string, nodeId: string, avant: readonly ImageListee[], apres: readonly ImageDuCorps[]): ImageDuCorps | undefined {
  const meme = apres.find((image) => image.imageId === imageId);
  if (meme !== undefined) return meme;
  const neuves = apres.filter((image) => image.nodeId === nodeId && !avant.some((connue) => connue.imageId === image.imageId));
  return neuves.length === 1 ? neuves[0] : undefined;
}

/**
 * La taille d'une image posée : la largeur demandée, ou celle de l'image
 * ramenée à la largeur du texte, et la hauteur que donnent ses proportions.
 */
function tailleAPoser(largeurDemandee: number | undefined, reference: Taille): { taille: Taille; reduite: boolean } {
  const reduite = largeurDemandee === undefined && reference.width > LARGEUR_DU_TEXTE_PX;
  const width = largeurDemandee === undefined ? Math.min(reference.width, LARGEUR_DU_TEXTE_PX) : Math.round(largeurDemandee);
  return { taille: { width, height: Math.max(1, Math.round((width * reference.height) / reference.width)) }, reduite };
}

export async function executeInsertImage(ctx: CanvasActionContext, input: InsertImageInput): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const poser = operationDuDocument(ctx.doc, 'create', 'image');
  if (poser === undefined) {
    return refus(input, pre, 'CAPABILITY_UNAVAILABLE',
      'insert_image requires editor.doc.create.image, and this document does not expose it. Nothing was written. Tell the lawyer an image cannot be inserted from here.',
      { action: 'stop', reason: 'The editor does not expose the image operation; resending would fail the same way.' });
  }
  const lu = await lireLesImagesEtLeCorps(ctx, input.action);
  if (!lu.ok) return refus(input, pre, lu.code, lu.message, { action: 'reinspect', reason: 'Nothing was written; the images could not be read.' });

  const { nodeId, placement } = input.at;
  const ancre = await resolveCanvasSelector(ctx.doc, { kind: 'nodeId', nodeId }, { requireUnique: true, inspectedPublicNodeIds: ctx.inspectedPublicNodeIds });
  if (!ancre.ok || !lu.projection.blocks.some((bloc) => bloc.identity === 'public' && bloc.nodeId === nodeId)) {
    const horsPortee = !ancre.ok && ancre.code === 'TARGET_NOT_IN_FINAL_INSPECTION';
    return refus(input, pre, horsPortee ? 'TARGET_NOT_IN_FINAL_INSPECTION' : 'IMAGE_ANCHOR_NOT_FOUND',
      horsPortee
        ? `insert_image refused before any write: block ${nodeId} (at.nodeId) was not published by your latest read. Read it first with canvas_inspect {selector:{kind:"nodeId",nodeId:"${nodeId}"}}, then resend the action.`
        : `insert_image refused before any write: no top-level block of the document body has the id ${nodeId} (at.nodeId). An image is placed before or after a paragraph, a heading, a list item or a whole table, not a paragraph inside a table cell. Run canvas_inspect, take the nodeId of the block next to which the image goes, then resend the action.`,
      { action: 'reinspect', reason: 'The block that anchors the image is not a readable top-level block; nothing was written.' });
  }

  const source = await resoudreLaSourceDImage(ctx, input.action, input.source, { toutes: lu.toutes, duCorps: lu.duCorps, nommees: nommerLesImages(lu.duCorps) });
  if (!source.ok) return refus(input, pre, source.code, source.message, source.recovery);
  const { taille, reduite } = tailleAPoser(input.width, source.image.taille);

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const historiqueAvant = await profondeurDHistorique(ctx);
  const reponse = await ecrire(ctx, 'insert_image:create.image', ctx.expectedRevision ?? pre.revision, (options) => poser({
    src: source.image.dataUri,
    alt: 'Image',
    size: { ...taille, unit: 'px' },
    at: { kind: placement, target: { kind: 'block', nodeType: ancre.target.nodeType, nodeId } },
  }, options));

  const post = await snapshot(ctx);
  const relu = await lireLesImagesEtLeCorps(ctx, input.action);
  const historiqueApres = await profondeurDHistorique(ctx);
  ctx.verifierEpoque('insert_image:receipt');
  const revisionsApres = idsDeRevision(post.trackedChanges);
  const suivi = revisionsApres.some((id) => !revisionsAvant.includes(id));

  // Le juge est la liste relue, jamais la réponse de l'éditeur (en-tête du module).
  const neuves = relu.ok ? relu.duCorps.filter((image) => !lu.toutes.some((connue) => connue.imageId === image.imageId)) : [];
  const posee = neuves.length === 1 ? neuves[0] : undefined;
  const blocsApres = relu.ok ? relu.projection.blocks : [];
  const rangDe = (id: string): number => blocsApres.findIndex((bloc) => bloc.identity === 'public' && bloc.nodeId === id);
  const rangDeLAncre = rangDe(nodeId);
  const rangDuNeuf = posee === undefined ? -1 : rangDe(posee.nodeId);
  const blocNeuf = posee !== undefined && !lu.projection.blocsDuCorps.has(posee.nodeId);
  const plageDeLAncre = blocsApres[rangDeLAncre]?.outputRange;
  // Après un tableau, les paragraphes de ses cellules peuvent s'intercaler dans la carte : ils sont dans sa plage.
  const aSaPlace = rangDuNeuf >= 0 && rangDeLAncre >= 0 && (placement === 'before'
    ? rangDuNeuf === rangDeLAncre - 1
    : rangDuNeuf > rangDeLAncre && blocsApres.slice(rangDeLAncre + 1, rangDuNeuf).every((bloc) => plageDeLAncre !== undefined && plageDeLAncre.start <= bloc.outputRange.start && bloc.outputRange.end <= plageDeLAncre.end));
  const apres = posee === undefined ? undefined : tailleDe(posee);
  const commentRelire = relu.ok ? '' : ` The images could not be read back (${relu.message})`;

  const image: CanvasImageReceipt | undefined = posee === undefined ? undefined : { imageId: posee.imageId, nodeId: posee.nodeId, change: 'inserted', sizeAfter: tailleDe(posee) };
  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    // L'image relue fait foi : une réponse en refus ou une exception après une écriture constatée n'est pas un échec.
    provider: posee === undefined ? reponse : { success: true },
    revisionsAvant,
    revisionsApres,
    ...(image === undefined ? {} : { image }),
    verification: [
      {
        name: 'image_inserted',
        passed: posee !== undefined && blocNeuf,
        message: `Exactly one new body image, carried by a new block, must be listed by the editor after the call; it lists ${neuves.length} new image(s)${posee !== undefined && !blocNeuf ? `, carried by block ${posee.nodeId} which already existed` : ''}.${commentRelire}`,
      },
      {
        name: 'image_placed',
        passed: aSaPlace,
        message: `The new image's paragraph must stand right ${placement} block ${nodeId}.`,
      },
      {
        name: 'image_sized',
        passed: apres !== undefined && memeTaille(apres, taille),
        message: `The new image must read ${enClair(taille)} (±${TOLERANCE_PX} px); it reads ${apres === undefined ? 'nothing: no single new image is listed' : enClair(apres)}.`,
      },
    ],
    effects: posee === undefined ? [] : [{
      kind: 'insert_image',
      reviewability: 'tracked',
      description: `Inserted image ${posee.imageId} (${enClair(tailleDe(posee))}, ${posee.placement}) from ${source.image.origine}, in new paragraph ${posee.nodeId} ${placement} block ${nodeId}, ${suivi ? 'as a tracked insertion the lawyer accepts or rejects' : 'written directly'}.`
        + `${reduite ? ` Its own width (${source.image.taille.width} px) was reduced to the text width (${LARGEUR_DU_TEXTE_PX} px).` : ''}`
        + ` You do not see its pixels: tell the lawyer where it stands and let them check it. ${annulation(historiqueAvant, historiqueApres, suivi ? 1 : 3)}`,
      nodeIds: [posee.nodeId],
    }],
  });
}

/** Remplace le contenu d'une image du corps, à sa place : le cadre garde sa largeur, sa hauteur suit les proportions de la nouvelle image. */
async function remplacerLeContenu(
  ctx: CanvasActionContext,
  input: EditImageInput & { source: NonNullable<EditImageInput['source']> },
  pre: CanvasSnapshot,
  lu: { toutes: ImageListee[]; duCorps: ImageDuCorps[]; projection: CanvasMarkdownProjection },
  cible: ImageDuCorps,
): Promise<CanvasActionReceipt> {
  const remplacer = operationDuDocument(ctx.doc, 'images', 'replaceSource');
  const regler = operationDuDocument(ctx.doc, 'images', 'setSize');
  if (remplacer === undefined || regler === undefined) {
    return refus(input, pre, 'CAPABILITY_UNAVAILABLE',
      `edit_image requires editor.doc.images.${remplacer === undefined ? 'replaceSource' : 'setSize'} to replace a picture, and this document does not expose it. Nothing was written. Tell the lawyer the picture of image ${input.imageId} cannot be replaced from here.`,
      { action: 'stop', reason: 'The editor does not expose the image operation; resending would fail the same way.' });
  }
  if (input.source.imageId === input.imageId) {
    return refus(input, pre, 'IMAGE_SOURCE_IS_TARGET',
      `edit_image wrote nothing: source.imageId and imageId are the same image (${input.imageId}), so there is no other picture to put in it. Give as source the image, case document or attachment that holds the new picture.`,
      { action: 'stop', reason: 'The source and the target are the same image.' });
  }
  const source = await resoudreLaSourceDImage(ctx, input.action, input.source, { toutes: lu.toutes, duCorps: lu.duCorps, nommees: nommerLesImages(lu.duCorps) });
  if (!source.ok) return refus(input, pre, source.code, source.message, source.recovery);

  const avant = tailleDe(cible);
  if (avant.width <= 0) {
    return refus(input, pre, 'IMAGE_PROPORTIONS_UNREADABLE',
      `edit_image refused before any write: image ${input.imageId} reads as ${enClair(avant)}, so the width its frame should keep is unknown. Remove it with edit_image {imageId, remove:true}, then insert the new picture with insert_image and an explicit width.`,
      { action: 'stop', reason: 'The frame of the image has no readable width.' });
  }
  const voulue: Taille = { width: avant.width, height: Math.max(1, Math.round((avant.width * source.image.taille.height) / source.image.taille.width)) };
  const mediaAvant = (await mediasDesImages(ctx.doc)).get(input.imageId);
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const historiqueAvant = await profondeurDHistorique(ctx);

  // La hauteur du cadre AVANT le contenu : l'ordre inverse fait relister l'image sous un autre identifiant sous suivi (en-tête du module).
  let ecritures = 0;
  let reponse: unknown = { success: true };
  // L'identifiant sous lequel l'éditeur liste l'image MAINTENANT, et la liste qui valait avant la dernière écriture :
  // sur une image au contenu déjà remplacé sous suivi, régler le cadre la fait relister sous un identifiant neuf.
  let courante = input.imageId;
  let listeesAvant: readonly ImageListee[] = lu.toutes;
  if (!memeTaille(voulue, avant)) {
    reponse = await ecrire(ctx, 'edit_image:images.setSize', ctx.expectedRevision ?? pre.revision, (options) => regler({ imageId: input.imageId, size: { ...voulue, unit: 'px' } }, options));
    if (!aRefuse(reponse)) {
      ecritures += 1;
      // Suivre l'image à travers le cadre : sans cela le contenu s'écrirait sous un identifiant mort (`TARGET_NOT_FOUND`),
      // et l'ancienne image resterait étirée dans le nouveau cadre. Régler une taille ne change aucun bloc : la carte du corps lue avant vaut encore.
      const listees = await listerLesImages(ctx.doc);
      const suivie = listees === undefined ? undefined : imageRelue(input.imageId, cible.nodeId, lu.toutes, imagesDuCorps(listees, lu.projection.blocsDuCorps));
      if (listees !== undefined && suivie !== undefined) {
        courante = suivie.imageId;
        listeesAvant = listees;
      }
    }
  }
  const cadreRegle = !aRefuse(reponse);
  if (cadreRegle) {
    // La première écriture a avancé la révision : la seconde présente celle qui vaut maintenant.
    const attendue = ecritures === 0 ? ctx.expectedRevision ?? pre.revision : await revisionCourante(ctx, pre.revision);
    const visee = courante;
    reponse = await ecrire(ctx, 'edit_image:images.replaceSource', attendue, (options) => remplacer({ imageId: visee, src: source.image.dataUri }, options));
    if (!aRefuse(reponse)) ecritures += 1;
  }

  const post = await snapshot(ctx);
  const relu = await lireLesImagesEtLeCorps(ctx, input.action);
  // Même mécanisme que le changement de taille : l'image sous son identifiant, ou la seule image neuve de son bloc.
  const relue = relu.ok ? imageRelue(courante, cible.nodeId, listeesAvant, relu.duCorps) : undefined;
  // L'identifiant que la lecture suivante de l'agent rendra : c'est lui que le reçu porte.
  const finale = relue?.imageId ?? courante;
  const mediaApres = (await mediasDesImages(ctx.doc)).get(finale);
  const historiqueApres = await profondeurDHistorique(ctx);
  ctx.verifierEpoque('edit_image:receipt');
  const revisionsApres = idsDeRevision(post.trackedChanges);
  const suivi = revisionsApres.some((id) => !revisionsAvant.includes(id));
  const apres = relue === undefined ? undefined : tailleDe(relue);
  const contenuChange = mediaApres !== undefined && mediaApres !== mediaAvant;
  const commentRelire = relu.ok ? '' : ` The images could not be read back (${relu.message})`;
  // Le cadre réglé puis le contenu refusé : l'ancienne image est étirée dans le nouveau cadre, le reçu le dit.
  const cadreSeul = cadreRegle && aRefuse(reponse) && ecritures === 1;
  const renommee = finale !== input.imageId ? ` The editor now lists this image under a new id, ${finale}: use that id from now on.` : '';

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: reponse,
    revisionsAvant,
    revisionsApres,
    image: { imageId: finale, nodeId: cible.nodeId, change: 'replaced', sizeBefore: avant, ...(apres === undefined ? {} : { sizeAfter: apres }) },
    verification: [
      {
        name: 'image_kept_in_place',
        passed: relue !== undefined && relue.nodeId === cible.nodeId,
        message: `Image ${finale} must still be listed in block ${cible.nodeId} after the call.${commentRelire}`,
      },
      {
        name: 'image_picture_replaced',
        passed: contenuChange,
        message: `Image ${finale} must point to a new media file after the call; it points to ${mediaApres ?? 'none the editor lists'} (${mediaAvant ?? 'unknown'} before).`,
      },
      {
        name: 'image_frame_sized',
        passed: apres !== undefined && memeTaille(apres, voulue),
        message: `Image ${finale} must read ${enClair(voulue)} (±${TOLERANCE_PX} px: its width kept, its height from the new picture's proportions); it reads ${apres === undefined ? 'nothing: the image is no longer listed' : enClair(apres)}.`,
      },
    ],
    effects: ecritures === 0 ? [] : [{
      kind: 'edit_image',
      reviewability: 'tracked',
      description: (cadreSeul
        ? `Only the frame of image ${input.imageId} was resized, from ${enClair(avant)} to ${enClair(apres ?? voulue)}: the editor then refused the new picture, so the OLD picture now stands in the new frame. Undo it before anything else.`
        : `Replaced the picture of image ${input.imageId} in block ${cible.nodeId} with ${source.image.origine}; the image keeps its place and its ${cible.placement} placement, its frame goes from ${enClair(avant)} to ${enClair(apres ?? voulue)}, ${suivi ? `as ${ecritures === 2 ? 'two tracked revisions (frame, picture)' : 'a tracked revision'} the lawyer accepts or rejects` : 'written directly'}. You do not see its pixels: let the lawyer check the result.`)
        + `${renommee} ${annulation(historiqueAvant, historiqueApres, ecritures)}`,
      nodeIds: [cible.nodeId],
    }],
  });
}

export async function executeEditImage(ctx: CanvasActionContext, input: EditImageInput): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const supprimer = operationDuDocument(ctx.doc, 'images', 'delete');
  const regler = operationDuDocument(ctx.doc, 'images', 'setSize');
  const operation = input.remove === true ? supprimer : regler;
  if (input.source === undefined && operation === undefined) {
    return refus(input, pre, 'CAPABILITY_UNAVAILABLE',
      `edit_image requires editor.doc.images.${input.remove === true ? 'delete' : 'setSize'}, and this document does not expose it. Nothing was written. Tell the lawyer image ${input.imageId} cannot be ${input.remove === true ? 'removed' : 'resized'} from here.`,
      { action: 'stop', reason: 'The editor does not expose the image operation; resending would fail the same way.' });
  }

  const lu = await lireLesImagesEtLeCorps(ctx, input.action);
  if (!lu.ok) return refus(input, pre, lu.code, lu.message, { action: 'reinspect', reason: 'Nothing was written; the images could not be read.' });
  const cible = lu.duCorps.find((image) => image.imageId === input.imageId);
  if (cible === undefined) {
    if (lu.toutes.some((image) => image.imageId === input.imageId)) {
      return refus(input, pre, 'IMAGE_NOT_IN_BODY',
        `edit_image refused before any write: image ${input.imageId} is not in the document body, it belongs to a header or a footer. edit_image only changes body images. For the firm's letterhead use set_letterhead; for the text of a header or footer use set_header_footer (it never deletes an image). ${nommerLesImages(lu.duCorps)}`,
        { action: 'stop', reason: 'Header and footer images are outside this verb.' });
    }
    return refus(input, pre, 'IMAGE_NOT_FOUND',
      `edit_image refused before any write: no image has the id ${input.imageId} in this document. ${nommerLesImages(lu.duCorps)} Resend the action with one of these ids, exactly as written.`,
      { action: 'retry', reason: 'Refused before any write: nothing was written and the inspected scope is intact. Resend with an image id from the message, without re-reading.' });
  }
  if (ctx.inspectedPublicNodeIds !== undefined && !ctx.inspectedPublicNodeIds.has(cible.nodeId)) {
    return refus(input, pre, 'IMAGE_BLOCK_NOT_INSPECTED',
      `edit_image refused before any write: image ${input.imageId} is carried by block ${cible.nodeId}, which your latest read did not publish. Read it first with canvas_inspect {selector:{kind:"nodeId",nodeId:"${cible.nodeId}"}}, look at what stands around the image, then resend the action.`,
      { action: 'reinspect', reason: 'The block that carries the image is outside the inspected scope; nothing was written.' });
  }
  if (input.source !== undefined) return remplacerLeContenu(ctx, { ...input, source: input.source }, pre, lu, cible);
  // Écarté plus haut : sans `source`, l'opération du geste demandé existe.
  if (operation === undefined) throw new Error(`edit_image : opération images.${input.remove === true ? 'delete' : 'setSize'} absente après son contrôle (image ${input.imageId}).`);

  const avant = tailleDe(cible);
  const demandee = input.size === undefined ? undefined : tailleDemandee(input.size, avant);
  if (input.size !== undefined) {
    if (demandee === undefined) {
      return refus(input, pre, 'IMAGE_PROPORTIONS_UNREADABLE',
        `edit_image refused before any write: image ${input.imageId} reads as ${enClair(avant)}, so its proportions cannot be kept from one side alone. Resend size with both width and height, in pixels.`,
        { action: 'retry', reason: 'Refused before any write: resend size with both width and height.' });
    }
    if (memeTaille(demandee, avant)) {
      return refus(input, pre, 'IMAGE_SIZE_UNCHANGED',
        `edit_image wrote nothing: image ${input.imageId} is already ${enClair(avant)}, the size requested (${enClair(demandee)}). There is nothing to resize.`,
        { action: 'stop', reason: 'The image already has the requested size.' });
    }
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const provider = await ecrire(ctx, `edit_image:${input.remove === true ? 'images.delete' : 'images.setSize'}`, ctx.expectedRevision ?? pre.revision, (options) => (
    demandee === undefined
      ? operation({ imageId: input.imageId }, options)
      : operation({ imageId: input.imageId, size: { ...demandee, unit: 'px' } }, options)
  ));

  const post = await snapshot(ctx);
  const relu = await lireLesImagesEtLeCorps(ctx, input.action);
  ctx.verifierEpoque('edit_image:receipt');
  const revisionsApres = idsDeRevision(post.trackedChanges);
  const suivi = revisionsApres.some((id) => !revisionsAvant.includes(id));
  const commun = {
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider,
    revisionsAvant,
    revisionsApres,
  };
  const commentRelire = relu.ok ? '' : ` The images could not be read back (${relu.message})`;

  if (demandee === undefined) {
    const encoreListee = relu.ok && relu.duCorps.some((candidat) => candidat.imageId === input.imageId);
    const image: CanvasImageReceipt = { imageId: input.imageId, nodeId: cible.nodeId, change: 'removed', sizeBefore: avant };
    return buildVerifiedReceipt({
      ...commun,
      image,
      verification: [{
        name: 'image_removed',
        passed: relu.ok && !encoreListee,
        message: `Image ${input.imageId} must be absent from the editor's image list read after the call.${commentRelire}`,
      }],
      effects: [{
        kind: 'edit_image',
        reviewability: 'tracked',
        description: `Removed image ${input.imageId} (${enClair(avant)}, ${cible.placement}) from block ${cible.nodeId}, ${suivi ? 'as a tracked deletion the lawyer accepts or rejects' : 'written directly'}. `
          + `${suiteApresRetrait(cible.nodeId, relu.ok ? relu.projection : undefined, relu.ok ? relu.duCorps : [], suivi)} To undo this call: undo_changes steps:1.`,
        nodeIds: [cible.nodeId],
      }],
    });
  }

  const relue = relu.ok ? imageRelue(input.imageId, cible.nodeId, lu.toutes, relu.duCorps) : undefined;
  const apres = relue === undefined ? undefined : tailleDe(relue);
  const renommee = relue !== undefined && relue.imageId !== input.imageId;
  const image: CanvasImageReceipt = {
    imageId: relue?.imageId ?? input.imageId, nodeId: cible.nodeId, change: 'resized', sizeBefore: avant, ...(apres === undefined ? {} : { sizeAfter: apres }),
  };
  return buildVerifiedReceipt({
    ...commun,
    image,
    verification: [{
      name: 'image_resized',
      passed: apres !== undefined && memeTaille(apres, demandee),
      message: `Image ${input.imageId} must read ${enClair(demandee)} (±${TOLERANCE_PX} px) in the editor's image list after the call; it reads ${apres === undefined ? 'nothing: the image is no longer listed' : enClair(apres)}.${commentRelire}`,
    }],
    effects: [{
      kind: 'edit_image',
      reviewability: 'tracked',
      description: `Resized image ${input.imageId} in block ${cible.nodeId} from ${enClair(avant)} to ${enClair(apres ?? demandee)}, ${suivi ? 'as a tracked change the lawyer accepts or rejects' : 'written directly'}.`
        + `${renommee ? ` The editor now lists this image under a new id, ${relue.imageId}: use that id from now on.` : ''} To undo this call: undo_changes steps:1.`,
      nodeIds: [cible.nodeId],
    }],
  });
}
